/* ============================================================================
   haiku-row — paging controls for the homepage products row

   WHY THIS EXISTS
   Measured on the live homepage: 36 product items in the DOM, a track whose
   scrollWidth is 2196px inside a 1440px viewport, and overflow-x: auto. So
   there is genuinely more in the row than fits — but the only affordance was
   a native scrollbar the design hides. Everything past the fold was reachable
   in principle and invisible in practice.

   These controls page the track by whole items. They are ADDED ONLY IF the
   content actually overflows, and are removed again if a resize makes the
   overflow go away — a control that does nothing is worse than no control.

   Native scrolling is untouched: trackpad, shift-wheel, touch and keyboard all
   still work. This adds a second way in, it does not replace the first.
   ========================================================================== */
(function () {
  'use strict';

  var cfg = window.haikuRowConfig || {};
  if (cfg.enabled === false) return;

  var LABEL_PREV = cfg.prevLabel || 'Previous products';
  var LABEL_NEXT = cfg.nextLabel || 'Next products';

  function track(sec) {
    // The scrolling element is whichever descendant actually overflows.
    var cands = sec.querySelectorAll('.css-slider, [class*="slider"], [class*="track"]');
    for (var i = 0; i < cands.length; i++) {
      var el = cands[i];
      var ox = getComputedStyle(el).overflowX;
      if ((ox === 'auto' || ox === 'scroll') && el.scrollWidth > el.clientWidth + 4) return el;
    }
    return null;
  }

  function step(el) {
    // One item, so paging lands on a boundary rather than mid-photograph.
    var item = el.querySelector('.product-item, [class*="product-item"]');
    var w = item ? item.getBoundingClientRect().width : 0;
    return w > 40 ? Math.round(w) : Math.round(el.clientWidth * 0.8);
  }

  function build(sec, el) {
    if (sec.querySelector('.hr-nav')) return sec.querySelector('.hr-nav');

    var nav = document.createElement('div');
    nav.className = 'hr-nav';

    ['prev', 'next'].forEach(function (dir) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'hr-nav__btn hr-nav__btn--' + dir;
      b.setAttribute('aria-label', dir === 'prev' ? LABEL_PREV : LABEL_NEXT);
      b.innerHTML = dir === 'prev'
        ? '<svg viewBox="0 0 22 22" width="22" height="22" fill="none" aria-hidden="true"><path d="M13.5 4.5L7 11l6.5 6.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        : '<svg viewBox="0 0 22 22" width="22" height="22" fill="none" aria-hidden="true"><path d="M8.5 4.5L15 11l-6.5 6.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      b.addEventListener('click', function () {
        el.scrollBy({ left: (dir === 'prev' ? -1 : 1) * step(el), behavior: 'smooth' });
      });
      nav.appendChild(b);
    });

    var head = sec.querySelector('.section-header') || sec.firstElementChild;
    if (head && head.parentNode) head.parentNode.insertBefore(nav, head.nextSibling);
    else sec.appendChild(nav);
    return nav;
  }

  /* ==========================================================================
     POINTER DRAG

     The row is a native `overflow-x: auto` scroller, so trackpad, touch,
     shift+wheel, keyboard and the arrows above all reach it. Grabbing it with
     the mouse did not, and that is the gesture a photograph rail invites.

     The theme ships a drag of its own inside <css-slider>, and it is unusable
     here for two reasons, both measured: it is disabled outright whenever the
     slider decides its navigation should hide (it adds `css-slider--no-drag`,
     and removing that class after boot does not bring the handlers back,
     because they are only bound at init); and what it does is PAGE — one
     mousemove calls changeSlide('next') — rather than move the rail under the
     hand. So this is our own, writing scrollLeft directly.

     Touch is deliberately left alone: the native momentum scroll is better
     than anything reimplemented here, and taking the gesture would cost it.
     ========================================================================= */

  function swallow(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function drag(el) {
    if (el.__hrDrag) return;
    el.__hrDrag = true;

    var down = false, moved = 0, startX = 0, startLeft = 0, id = null;

    el.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      down = true;
      moved = 0;
      startX = e.clientX;
      startLeft = el.scrollLeft;
      id = e.pointerId;
    });

    el.addEventListener('pointermove', function (e) {
      if (!down || e.pointerId !== id) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > moved) moved = Math.abs(dx);
      /* 3px of slop: below it this is a click on a product, not a drag, and
         claiming the pointer would break the link. */
      if (moved <= 3) return;
      if (!el.classList.contains('hr-dragging')) {
        el.classList.add('hr-dragging');
        try { el.setPointerCapture(id); } catch (err) {}
      }
      el.scrollLeft = startLeft - dx;
      e.preventDefault();
    });

    function release() {
      if (!down) return;
      down = false;
      el.classList.remove('hr-dragging');
      if (id !== null) { try { el.releasePointerCapture(id); } catch (err) {} }
      id = null;
      /* A real drag ends over a product link and would otherwise open it.
         Capture-phase, and removed on the next tick so only THIS click dies. */
      if (moved > 6) {
        el.addEventListener('click', swallow, true);
        window.setTimeout(function () {
          el.removeEventListener('click', swallow, true);
        }, 0);
      }
    }

    el.addEventListener('pointerup', release);
    el.addEventListener('pointercancel', release);
    el.addEventListener('lostpointercapture', release);

    /* Without this the browser starts its own image drag off a photograph and
       the rail stops following the hand mid-gesture. */
    el.addEventListener('dragstart', function (e) {
      if (down) e.preventDefault();
    });
  }

  /* Grey out an end rather than hiding it, so the row does not reflow as you
     page and the control stays where the hand expects it. */
  function sync(el, nav) {
    var atStart = el.scrollLeft <= 2;
    var atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
    nav.querySelector('.hr-nav__btn--prev').disabled = atStart;
    nav.querySelector('.hr-nav__btn--next').disabled = atEnd;
  }

  function boot() {
    var secs = document.querySelectorAll('.shopify-section');
    for (var i = 0; i < secs.length; i++) {
      var sec = secs[i];
      if (!sec.querySelector('.products-slider, [class*="products-slider"]')) continue;
      sec.classList.add('hr');

      var el = track(sec);
      var existing = sec.querySelector('.hr-nav');

      if (!el) {
        // No overflow (or not any more) — do not leave a dead control behind.
        if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
        sec.removeAttribute('data-hr-nav');
        continue;
      }

      var nav = build(sec, el);
      sec.setAttribute('data-hr-nav', '1');
      sync(el, nav);

      drag(el);

      if (!el.__hrBound) {
        el.__hrBound = true;
        el.addEventListener('scroll', function () { sync(el, nav); }, { passive: true });
        window.addEventListener('resize', function () { sync(el, nav); });
      }
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // The row builds its items after first paint on some loads.
  window.setTimeout(boot, 800);
  window.setTimeout(boot, 2000);
  window.addEventListener('resize', boot);
})();
